'use client';

import { useBunja } from 'bunja/react';
import { useAtomValue } from 'jotai';
import { useSearchParams } from 'next/navigation';
import { useEffect } from 'react';

import { authBunja } from '@flipster/features/src/shared/auth/state';
import { earnAbTestBunja, earnSummaryBunja } from '@flipster/features/src/shared/earn/state';
import { usePathname, useRouter } from '@flipster/i18n/routing';
import { useOverlay } from '@flipster/overlay';
import { css } from '@flipster/styled-system/css';
import { stack } from '@flipster/styled-system/patterns';

import WalletModal from '@web/app/[locale]/(web)/_modal/WalletModal';

import Banner from './Banner';
import ExchangeWinnersSection from './ExchangeWinnersSection';
import Faq from './Faq';
import BeforeParticipationEarnAprSection from './apr/before-participation-earn-apr-section';
import EarnActionModal from './apr/earn-action-modal';
import TradingBonusBoostSection from './apr/trading-bonus-boost-section';
import YourEarnAprSection from './apr/your-earn-apr-section';
import EarnHistorySection from './history/EarnHistorySection';
import EarningsOverview from './overview';

const Page = () => {
  const pathname = usePathname();
  const router = useRouter();
  const searchParams = useSearchParams();
  const actionParam = searchParams.get('action');

  const overlay = useOverlay();
  const { userAtom } = useBunja(authBunja);
  const { showTradingBonusBoostSectionAtom } = useBunja(earnAbTestBunja);
  const { earnSummaryAtom } = useBunja(earnSummaryBunja);
  const earnSummary = useAtomValue(earnSummaryAtom);

  const user = useAtomValue(userAtom);
  const showTradingBonusBoostSection = useAtomValue(showTradingBonusBoostSectionAtom);
  const hasFirstDeposit = user && user.firstDepositAt;

  useEffect(() => {
    if (actionParam) {
      const hasCurrency = earnSummary?.currencies.some((c) => c.currency === actionParam.toUpperCase());
      if (!hasCurrency) return;
      overlay.open(<EarnActionModal currency={actionParam.toUpperCase()} />);

      // Remove mint query parameter
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.delete('action');
      const newUrl = `${pathname}${newSearchParams.toString() ? `?${newSearchParams.toString()}` : ''}`;
      router.replace(newUrl);
    }
  }, [pathname, actionParam, router, searchParams, overlay, earnSummary]);

  return (
    <>
      <main
        className={css({
          w: '100%',
          px: 16,
          maxW: 992,
          mx: 'auto',
          pb: { base: 80, tablet: 150 },
          pt: { base: 32, tablet: 80 },
        })}
      >
        <h1
          className={css({
            textStyle: 'Display/XL',
            color: 'content.default.level-4',
            mb: { base: 20, tablet: 40 },
          })}
        >
          Earn
        </h1>
        <div className={stack({ gap: { base: 32, tablet: 64 } })}>
          <Banner />
          {hasFirstDeposit && <EarningsOverview />}
          {hasFirstDeposit && showTradingBonusBoostSection && <TradingBonusBoostSection />}
          {hasFirstDeposit && <YourEarnAprSection />}
          {!hasFirstDeposit && <BeforeParticipationEarnAprSection />}
          <ExchangeWinnersSection />
          {hasFirstDeposit && <EarnHistorySection />}
          <Faq />
        </div>
      </main>
      <WalletModal />
    </>
  );
};

export default Page;
